/*------------------------------------------------------------------------------
   About      : Reading a batch response (epic DA-697, II-FE-5)

                Every insight write is a batch and **collects per-item
                failures**: `{ data: { insights, error? } }` where `error` is
                `[ { "<code>": "message" } ]`. One bad row does not fail the
                batch.

                So the two naive readings are both wrong:
                  - "no transport error ⇒ success" reports 9 published when 2
                    failed;
                  - "any error ⇒ failure" reports total failure when 7 of 9 went
                    through, and the user re-runs an action that already
                    partially applied.

                This turns a response into an honest three-way outcome.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { InsightApiResult }  from '../api/insightAPI'
import { InsightBatchResponse,
         InsightItemError,
         InsightRecord,
         UpdateAllResponse } from '../types/insightTypes'

export type BatchOutcomeKind = 'SUCCESS' | 'PARTIAL' | 'FAILURE'

export interface BatchFailure {
  /** The insight `code` (or whatever key the server used) the failure is for. */
  key     : string
  message : string
}

export interface BatchOutcome {
  kind      : BatchOutcomeKind
  succeeded : number
  failed    : number
  /** Total the caller attempted, so "7 of 9" is expressible. */
  attempted : number
  failures  : BatchFailure[]
  /** Records the server returned, for re-seeding the store. */
  insights  : InsightRecord[]
  /** Ready-to-render summary. */
  message   : string
}

/** Flattens `[ { "NC-71-N": "reason" } ]` into a list. */
export function flattenItemErrors(errors: InsightItemError[] | undefined): BatchFailure[] {
  if (!errors?.length) return []
  return errors.flatMap((entry) =>
    Object.entries(entry).map(([key, message]) => ({ key, message : String(message) })),
  )
}

/**
 * Interprets a batch response.
 *
 * `attempted` is passed in rather than inferred: the server returns only the
 * rows it succeeded on, so `insights.length + failures.length` can under-count
 * when a row fails without producing an error entry, and the user would be told
 * fewer items were touched than they selected.
 */
export function readBatchOutcome(
  result    : InsightApiResult<InsightBatchResponse | UpdateAllResponse>,
  attempted : number,
  verb      : string,
): BatchOutcome {

  // Transport, auth or RBAC failure — nothing was applied.
  if (result.error) {
    return {
      kind      : 'FAILURE',
      succeeded : 0,
      failed    : attempted,
      attempted,
      failures  : [],
      insights  : [],
      message   : result.error.message || `Failed to ${verb}.`,
    }
  }

  const failures = flattenItemErrors(result.data?.error)
  const insights = result.data?.insights ?? []

  // The server reports successes by returning records, but some routes return
  // `results` instead, so fall back to "attempted minus failed" rather than
  // claiming zero succeeded.
  const succeeded = insights.length || Math.max(attempted - failures.length, 0)

  if (!failures.length) {
    return {
      kind      : 'SUCCESS',
      succeeded,
      failed    : 0,
      attempted,
      failures,
      insights,
      message   : `${succeeded} ${succeeded === 1 ? 'insight' : 'insights'} ${verb}.`,
    }
  }

  if (succeeded === 0) {
    return {
      kind      : 'FAILURE',
      succeeded : 0,
      failed    : failures.length,
      attempted,
      failures,
      insights,
      message   : `Could not ${verb} any of the ${attempted} selected ${attempted === 1 ? 'insight' : 'insights'}.`,
    }
  }

  return {
    kind      : 'PARTIAL',
    succeeded,
    failed    : failures.length,
    attempted,
    failures,
    insights,
    message   : `${succeeded} of ${attempted} ${verb}; ${failures.length} failed.`,
  }
}
